import React, { useState, useEffect } from "react";
import classNames from 'classnames'

import MultiSelectDropdownOption from './MultiSelectDropdownOption'
import MultiSelectDropdownFilter from './MultiSelectDropdownFilter'
import { MultiSelectDropdownAllOption } from "./MultiSelectDropdownAllOption";
import { usePrevious } from "../hooks/usePrevious";

export default function MultiSelectDropdownBody(props) {

    const { options, selectAllOption, onLoadAllSelected, filterable, maxAllowedSelections, onChange, show, externalDataReference, defaultSelected, sortSelectedFirst = false } = props
    const [isFiltered, setFiltered] = useState(false)

    const [selectedOptions, setSelectedOptions] = useState({})
    const [numberSelectedOptions, setNumberSelectedOptions] = useState({})
    const [allSelected, setAllSelected] = useState(onLoadAllSelected)
    const [viewableOptions, setViewableOptions] = useState([]);

    const prevOptions = usePrevious(options)
    const prevDefaultSelected = usePrevious(defaultSelected)
    // setting all selected options values to false to start with when options prop changes.
    useEffect(() => {
        if (options.length !== Object.keys(selectedOptions).length) {
            let newSelectedOptions = {}

            options.forEach(option => {
                newSelectedOptions[option.value] = onLoadAllSelected;
            })

            // setting any default values if any
            if (defaultSelected && defaultSelected.length > 0) {
                defaultSelected.forEach(option => {
                    newSelectedOptions[option.value] = true
                })
            }
            
           onChange(Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]))
            setSelectedOptions(newSelectedOptions);
        }
    }, [options])

    // Re-sync internal selection state whenever the controlled defaultSelected prop changes
    // externally after mount (e.g. a parent swapping the selection to "select all" or to a
    // derived set of options) - the "options changed" effects above only run when the options
    // list itself changes, so without this, an externally driven selection wouldn't be
    // reflected in the dropdown's checkboxes.
    useEffect(() => {
        if (JSON.stringify(prevDefaultSelected) === JSON.stringify(defaultSelected)) return;

        let newSelectedOptions = {}

        options.forEach(option => {
            newSelectedOptions[option.value] = false;
        })

        if (defaultSelected && defaultSelected.length > 0) {
            defaultSelected.forEach(option => {
                newSelectedOptions[option.value] = true;
            })
        }

        let numberSelected = Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]).length;
        setNumberSelectedOptions(numberSelected);

        setSelectedOptions(newSelectedOptions);
        onChange(Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]));
    }, [JSON.stringify(defaultSelected)])

    useEffect(() => {
        if (options && JSON.stringify(prevOptions) !== JSON.stringify(options)) {
            let newSelectedOptions = {}

            options.forEach(option => {
                newSelectedOptions[option.value] = onLoadAllSelected;
            })

            // setting any default values if any
            if (defaultSelected && defaultSelected.length > 0) {
                defaultSelected.forEach(option => {
                    newSelectedOptions[option.value] = true
                })
            }

            onChange(Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]))
            setSelectedOptions(newSelectedOptions);
        }
    }, [JSON.stringify(options)])
    // handles when an option is clicked
    function handleOptionClick(value) {

        let newSelectedOptions = { ...selectedOptions }; // Create a new object instead of mutating
        newSelectedOptions[value] = !newSelectedOptions[value];

        let numberSelected = Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]).length;
        // setNumberSelectedOptions(Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]).length)
        setNumberSelectedOptions(numberSelected);

        if (Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]).length <= maxAllowedSelections) {

            setSelectedOptions(newSelectedOptions)

            // check if every option has been selected
            let isAllSelected = Object.keys(newSelectedOptions).every(key => newSelectedOptions[key])
            setAllSelected(isAllSelected)
            onChange(Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]))
        }
    }

    // handles when All option is selected
    function handleAllSelected() {
        // Use the visible selection state to determine toggle direction,
        // since the "All" checkbox displays allVisibleSelected — not allSelected.
        let activeOptions = isFiltered ? viewableOptions : options;
        let currentlyAllVisible = activeOptions.length > 0 && activeOptions.every(option => selectedOptions[option.value.toString()]);
        let isSelected = !currentlyAllVisible;

        // When a filter is active, only toggle the visible/filtered options.
        // When no filter is active, toggle all options.
        let keysToToggle = isFiltered
            ? viewableOptions.map(option => option.value.toString())
            : Object.keys(selectedOptions);

        let newSelectedOptions = { ...selectedOptions };
        keysToToggle.forEach(key => {
            newSelectedOptions[key] = isSelected;
        });

        let totalSelected = Object.keys(newSelectedOptions).filter(key => newSelectedOptions[key]).length;
        setNumberSelectedOptions(totalSelected);

        let isAllSelected = isSelected && activeOptions.every(option => newSelectedOptions[option.value.toString()]);
        setSelectedOptions(newSelectedOptions);
        setAllSelected(isAllSelected);
        onChange(Object.keys(newSelectedOptions).filter(option => newSelectedOptions[option]));
    }

    function handleFilterChanged(filter) {
        if (filter === '') {
            setFiltered(false)
        } else {
            let newViewableOptions = options;

            newViewableOptions = newViewableOptions.filter(option => {
                return option.label.toLowerCase().includes(filter)
            });

            setViewableOptions(newViewableOptions)
            setFiltered(true)
        }
    }

    let optionsToUse = isFiltered ? viewableOptions : options

    // When sortSelectedFirst is true, keep options flagged with `pinned` at the top. We sort a
    // copy so internal state (selectedOptions) stays stable. Selection state does not affect
    // ordering - checking/unchecking items does not reorder the list.
    if (sortSelectedFirst && !isFiltered) {
        optionsToUse = optionsToUse.slice().sort(function (a, b) {
            var aPinned = a.pinned ? 0 : 1;
            var bPinned = b.pinned ? 0 : 1;
            return aPinned - bPinned;
        });
    }
    // The "All" checkbox should appear checked only when every currently visible option is selected
    let allVisibleSelected = optionsToUse.length > 0 && optionsToUse.every(option => selectedOptions[option.value.toString()]);
    let className = classNames('multi-select-dropdown-body', { 'hidden': !show });

    return (
        <div className={className}>
            <ul>
                <MultiSelectDropdownFilter show={filterable} handleFilterChanged={handleFilterChanged} />
                <MultiSelectDropdownAllOption show={selectAllOption} allSelected={allVisibleSelected} handleAllSelected={handleAllSelected} />
                {optionsToUse.map((option) => {
                    return (
                        <MultiSelectDropdownOption
                            selected={selectedOptions[option.value]}
                            key={option.value}
                            label={option.label}
                            value={option.value}
                            handleOptionClick={handleOptionClick}
                            disabled={externalDataReference ? externalDataReference.indexOf(option.value.toString()) > -1 : false}
                            maxReached={numberSelectedOptions >= maxAllowedSelections}
                        />
                    )
                })}
            </ul>
        </div>
    )
}